import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useRepository } from '@/Application/Providers/Repository';
import { useBuyNowSkuHistory } from '@/Application/Selectors/buyNow/useBuyNowSkuHistory';
import { store } from '@/Infrastructure/State';
import { getShippingGrouping } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import { extractCartItemSkus } from '@/modules/shared/utils/getCartSkus';
import useGetShippingGrouping from '@/sections/shippingMethods/hooks/useGetShippingGrouping';
import useSetBuyNowShippingMethods from '@/sections/shippingMethods/hooks/useSetBuyNowShippingMethods';

import useBuyNowErrorBoundary from './useBuyNowErrorBoundary';

const useBuyNowPageInitializer = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const repositories = useRepository();

  const { shippingMethods, isLoading: isLoadingShippingMethods } =
    useGetShippingGrouping();
  const { setDefaultShippingOptions } = useSetBuyNowShippingMethods();
  const { initializeSku } = useBuyNowSkuHistory();

  useBuyNowErrorBoundary();

  const hasSetDefaultShippingOptions = useRef(false);
  const isSettingDefaultOptions = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const initializeAdditionalData = async () => {
      try {
        if (!cartId) return;

        const cartPromise = repositories.buyNowRepository?.getCart(cartId);
        repositories.buyNowRepository?.getAlternativePayments(cartId);
        repositories.buyNowRepository?.getAvailablePaymentMethods(cartId);

        const cart = await cartPromise;

        if (!cart) return;

        const cartSkus = extractCartItemSkus(cart);
        await store.dispatch(
          getShippingGrouping({ params: cartSkus, cartId: cartId })
        );
      } catch (error) {
        console.error('Error initializing buy now page:', error);
        setIsInitializing(false);
      }
    };

    initializeAdditionalData();
  }, [cartId, repositories]);

  useEffect(() => {
    const setDefaultOptions = async () => {
      const deliveryGroups = shippingMethods?.deliveryGroups;
      const hasDeliveryGroups = deliveryGroups && deliveryGroups.length > 0;

      if (
        !isLoadingShippingMethods &&
        shippingMethods &&
        hasDeliveryGroups &&
        !hasSetDefaultShippingOptions.current &&
        !isSettingDefaultOptions.current
      ) {
        isSettingDefaultOptions.current = true;
        try {
          const firstProduct = deliveryGroups?.[0]?.productList?.[0];
          if (firstProduct?.productSKU) {
            initializeSku(firstProduct?.productSKU);
          }

          await setDefaultShippingOptions(shippingMethods?.deliveryGroups);
          hasSetDefaultShippingOptions.current = true;
        } catch (error) {
          console.error('Error setting default shipping options:', error);
        } finally {
          setIsInitializing(false);
          isSettingDefaultOptions.current = false;
        }
      } else if (
        !isLoadingShippingMethods &&
        hasSetDefaultShippingOptions.current
      ) {
        setIsInitializing(false);
      }
    };

    setDefaultOptions();
  }, [
    shippingMethods,
    isLoadingShippingMethods,
    setDefaultShippingOptions,
    initializeSku
  ]);

  return { isInitializing };
};

export default useBuyNowPageInitializer;
